import React, { useRef, useMemo, useState, useEffect } from 'react';
import {
  Modal,
  Button,
  FormGroup,
  FormControl,
  ControlLabel,
  Checkbox,
  Popover,
  OverlayTrigger,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import qs from 'qs';
import Alert from '../../../../../../components/Alert';
import TipTap from '../../../../../../components/TipTap';
import { delay, getErrorMessage } from '../../../../../../utils/helper';
import { activateModule, deactivateModule, editModule } from '../../../../../../services/endpoints/settings';
import Loader from '../../../../../../components/Loaders/Loader';

const ticketToolbar = [['bold', 'italic', 'blockquote'], ['ul', 'ol'], ['hr']];

const EditModulesModal = ({ data, access, onSubmit, onHide }) => {
  const [form, setForm] = useState({
    isLoading: false,
    error: null,
    success: false,
  })

  const { error, isLoading, success } = form;

  const formNode = useRef(null);
  let ref = useRef(null);
  const formItems = useMemo(() => (data?.form ? data?.form?.map((item) => item) : null), [data]);

  const [wysiwygContent, setWysiwygContent] = useState('');

  const onChangeWysiwyg = (value) => {
    setWysiwygContent(value);
  };

  const onDeactivate = async () => {
    ref?.handleHide()
    setForm((prevState) => ({
      ...prevState,
      isLoading: true,
    }))
    try {
      await deactivateModule(data.id);
      setForm((prevState) => ({
        ...prevState,
        success: true,
      }))
      await delay(500);
      onSubmit()
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      setForm((prevState) => ({
        ...prevState,
        isLoading: false,
        success: false,
        error: errorMessage,
      }))
    }
  }

  const onSaveChanges = async (payload) => {
    setForm((prevState) => ({
      ...prevState,
      isLoading: true,
    }))
    try {
      data.type === 'activate' ? await activateModule(data.id, payload) : await editModule(data.id, payload);
      setForm((prevState) => ({
        ...prevState,
        success: true,
      }))
      await delay(500);
      onSubmit()
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      setForm((prevState) => ({
        ...prevState,
        isLoading: false,
        success: false,
        error: errorMessage,
      }))
    }
  }

  const onSubmitForm = (event) => {
    event?.preventDefault();
    const current = formNode?.current;
    const forData = new FormData(current);
    const payload = {};
    for (const name of forData.keys()) {
      if (formNode.current?.[name]) {
        payload[name] = formNode.current[name].value;
      }
    }
    const parsePayload = qs.parse(payload);
    if (parsePayload?.enabled_currencies) {
      const defCurrency = data?.form?.find((f) => f.code === 'enabled_currencies')?.items?.[0];
      parsePayload.enabled_currencies = [defCurrency?.value, ...Object.values(parsePayload?.enabled_currencies)];
    }
    const wysiwyg = formItems?.find((item) => item.type === 'wysiwyg');
    if (wysiwyg) {
      parsePayload[wysiwyg.code] = wysiwygContent;
    }
    onSaveChanges({
      form_fields: parsePayload,
    });
  };

  useEffect(() => {
    const wysiwyg = formItems?.find((item) => item.type === 'wysiwyg');
    if (wysiwyg) {
      setWysiwygContent(wysiwyg.value || wysiwyg.default_value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, formItems]);

  const popover = (
    <Popover id="popover-trigger-click-root-close" title="" className="text-center">
      {/* <div className="m-b">Are you sure that<br /> you want to deactivate module?</div> */}
      <Button bsStyle="primary" bsSize="sm" onClick={onDeactivate}>Confirm</Button>
      <Button bsSize="sm" className="ml-8" onClick={() => ref?.handleHide()}>Cancel</Button>
    </Popover>
  );

  return (
    <form ref={formNode} onSubmit={onSubmitForm}>
      <Modal.Header
        closeButton
        onHide={onHide}
      >
        <Modal.Title>{data?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert text={error} />}
        {isLoading ? <Loader success={success} /> : null}
        <>
          {formItems?.map((item, index) => {
            switch (item.type) {
            case 'input':
              return (
                <FormGroup key={item.code}>
                  <ControlLabel htmlFor={item.code}>{item.label}</ControlLabel>
                  <FormControl
                    type={item.input_type}
                    name={item.code}
                    defaultValue={item.value || item.default_value}
                    autoFocus={index === 0}
                  />
                </FormGroup>
              );

            case 'textarea':
              return (
                <FormGroup key={item.code}>
                  <ControlLabel htmlFor={item.code}>{item.label}</ControlLabel>
                  <FormControl
                    rows={7}
                    componentClass="textarea"
                    name={item.code}
                    defaultValue={item.value || item.default_value}
                  />
                </FormGroup>
              );

            case 'wysiwyg':
              return (
                <FormGroup key={item.code}>
                  <ControlLabel htmlFor={item.code}>{item.label}</ControlLabel>
                  <TipTap
                    key={item.code}
                    toolbar={ticketToolbar}
                    content={item.value || item.default_value}
                    onChange={onChangeWysiwyg}
                  />
                </FormGroup>
              );

            case 'select':
              return (
                <FormGroup key={item.code}>
                  <ControlLabel htmlFor={item.code}>{item.label}</ControlLabel>
                  <FormControl
                    componentClass="select"
                    name={item.code}
                    defaultValue={item.value || item.default_value}
                    autoFocus={index === 0}
                  >
                    {item?.select_items?.map((select) => (
                      <option key={select.value} value={select.value}>
                        {select.label}
                      </option>
                    ))}
                  </FormControl>
                </FormGroup>
              );

            case 'checkbox_group':
              const values = item?.value ? Object.values(item?.value) : [item?.items?.[0]?.value];
              return (
                <FormGroup key={item.code}>
                  <ControlLabel>{item.label}</ControlLabel>
                  <div className="checkboxes-group">
                    {item?.items?.map((el, index) => (
                      <Checkbox
                        key={el.value + index}
                        className={classNames('', { grey: index === 0 })}
                        name={`${item.code}[${el.value}]`}
                        defaultChecked={values?.includes(el?.value)}
                        defaultValue={el.value}
                        disabled={index === 0}
                        inline
                      >
                        {' '}
                        {el.label}
                      </Checkbox>
                    ))}
                  </div>
                </FormGroup>
              );

            case 'text':
              return <p key={item.code}>{item.value}</p>;

            case 'html':
              return (
                <div
                  dangerouslySetInnerHTML={{ __html: item.value }}
                />
              );

            default:
              return null;
            }
          })}
        </>
      </Modal.Body>
      <Modal.Footer className="custom__modal-footer">
        {data?.type === 'edit' ? (
          <Button bsStyle="primary" type="submit" disabled={isLoading} onClick={onSubmitForm}>
            <span>Save changes</span>
          </Button>
        ) : null}
        {data?.type === 'activate' ? (
          <Button bsStyle="primary" type="submit" disabled={isLoading} onClick={onSubmitForm}>
            <span>Activate</span>
          </Button>
        ) : null}
        <Button
          onClick={onHide}
        >
          <span>Cancel</span>
        </Button>
        {data?.type === 'edit' && access?.modules_deactivate ? (
          <div className="pull-right">
            <OverlayTrigger ref={(r) => (ref = r)} trigger="click" rootClose placement="top" overlay={popover}>
              <Button
                disabled={isLoading}
                bsStyle="link"
                className="modal-delete-btn"
              >
                Deactivate
              </Button>
            </OverlayTrigger>
          </div>
        ) : null}
      </Modal.Footer>
    </form>
  );
};

EditModulesModal.propTypes = {
  data: PropTypes.object,
  access: PropTypes.object,
  onHide: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default EditModulesModal;
